import { forwardRef, memo, useRef } from 'react';
import { useTranslation } from 'react-i18next';

import { Empty, Layout } from 'antd';
import { clsx } from 'clsx';

import { default as classes } from './_.module.scss';
import { ChatContent } from './chat-content';
import { ChatHeader } from './chat-header';
import { ChatSender } from './chat-sender';
import { ChatSider } from './chat-sider';

import { useAuthContext } from '~/common/contexts/auth';
import { usePopupContext } from '~/common/contexts/knowledge/';

/**
 * Popup Component View
 *
 * This component renders the main UI for the Popup feature, including:
 * - A header with controls and mode switching
 * - A sidebar for managing conversations
 * - A chat interface for sending and receiving messages
 *
 * The component uses hooks to manage state, events, and computed values.
 */
export default memo(
  forwardRef(() => {
    const { states, events } = usePopupContext();
    const { tokenError } = useAuthContext();

    const { contextHolder, sidebarOpen, profileData, hasMenu } = states;
    const { handleOpenSidebar, handleCloseSidebar, handleAddConversation, onReload, onClose } = events;

    // Ref cho root container
    const rootRef = useRef<HTMLDivElement>(null);

    const { Content, Footer } = Layout;

    const { t } = useTranslation('error');
    const { t: common } = useTranslation('common');

    /**
     * Renders the main layout of the Popup component.
     *
     * Includes:
     * - Header with controls
     * - Sidebar for conversations (Drawer in popup, fixed in fullscreen)
     * - Chat interface with messages and sender
     */
    return (
      <Layout
        ref={rootRef}
        className='relative h-full'
      >
        {contextHolder}

        {hasMenu && !tokenError && (
          <ChatSider
            open={sidebarOpen}
            onClose={handleCloseSidebar}
            onReload={onReload}
            getContainer={() => rootRef.current}
          />
        )}

        <ChatHeader
          logoUrl={profileData?.sas_token}
          chatbotName={profileData?.name}
          onOpenSidebar={handleOpenSidebar}
          onAddConversation={handleAddConversation}
          onClose={onClose}
          hasMenu={hasMenu}
        />

        <Content className='flex overflow-auto bg-white'>
          {tokenError ? (
            <div className='flex h-full items-center justify-center'>
              <Empty description={t('error.401')} />
            </div>
          ) : (
            <ChatContent />
          )}
        </Content>

        <Footer className={clsx('bg-white! px-4.5! pt-2! pb-0!', classes.footer)}>
          <ChatSender disabled={tokenError} />
          <span className='mt-2 block text-center text-[12px] text-gray-500'>{common('chat.genAI')}</span>
        </Footer>
      </Layout>
    );
  }),
);
